import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  ReferralTrackingPayload,
  GetReferralTrackingApiResponse,
  GetReferralPayoutsApiResponse,
  ReferralUpdateAction,
  GenericError,
  JobsPayload,
  Status,
} from 'src/types/referral-v2';
import type {Task} from 'src/types/data-export-report';
import {thunkify as flow} from 'src/utils/thunks';
import {key, fetching} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api';
import {DEFAULT_ERROR} from 'src/action-creators/referral-v2/referral-jobs';
import {AnalyticsService} from 'src/analytics';
import {showApiError} from 'src/action-creators/modal';
import {toastApi} from '@spaced-out/ui-design-system/lib/components/index';
import {toastMessage} from 'src/components/referral-v2/tracking/referral-confirm-modal';


export const REFERRAL_TRACKING_LOADING = 'referral/referral-tracking-loading';
export const REFERRAL_TRACKING = 'referral/referral-tracking';
export const REFERRAL_TRACKING_ERROR = 'referral/referral-tracking-error';

export const REFERRAL_PAYOUTS_LOADING = 'referral/referral-payouts-loading';
export const REFERRAL_PAYOUTS = 'referral/referral-payouts';
export const REFERRAL_PAYOUTS_ERROR = 'referral/referral-payouts-error';

export const REFERRAL_TRACKING_UPDATE_ACTION_LOADING =
  'referral/referral-tracking-update-action-loading';
export const REFERRAL_TRACKING_UPDATE_ACTION_ERROR =
  'referral/referral-tracking-update-action-error';
export const REFERRAL_TRACKING_UPDATE_ACTION_STATUS =
  'referral/referral-tracking-update-action-status';

export const RECEIVE_TIME_SHEET_DATA = 'referral/receive-time-sheet-data';

type ReferralTrackingAction = {
  type: typeof REFERRAL_TRACKING;
  payload: GetReferralTrackingApiResponse;
};

type ReferralTrackingLoadingAction = {
  type: typeof REFERRAL_TRACKING_LOADING;
};

type ReferralTrackingErrorAction = {
  type: typeof REFERRAL_TRACKING_ERROR;
  payload: GenericError | null | undefined;
};

type ReferralPayoutsAction = {
  type: typeof REFERRAL_PAYOUTS;
  payload: GetReferralPayoutsApiResponse;
};
type ReferralPayoutsLoadingAction = {
  type: typeof REFERRAL_PAYOUTS_LOADING;
};

type ReferralPayoutsErrorAction = {
  type: typeof REFERRAL_PAYOUTS_ERROR;
  payload: GenericError | null | undefined;
};

type ReferralTrackingUpdateLoadingAction = {
  type: typeof REFERRAL_TRACKING_UPDATE_ACTION_LOADING;
};

type ReferralTrackingUpdateErrorAction = {
  type: typeof REFERRAL_TRACKING_UPDATE_ACTION_ERROR;
  payload: GenericError | null | undefined;
};

type ReferralTrackingUpdateStatusAction = {
  type: typeof REFERRAL_TRACKING_UPDATE_ACTION_STATUS;
  payload: Status | null | undefined;
};

type TimeSheetDataAction = {
  type: typeof RECEIVE_TIME_SHEET_DATA;
  payload: Task | null | undefined;
};

export type ReferralTrackingActions =
  | ReferralPayoutsAction
  | ReferralPayoutsLoadingAction
  | ReferralPayoutsErrorAction
  | ReferralTrackingAction
  | ReferralTrackingLoadingAction
  | ReferralTrackingErrorAction
  | ReferralTrackingUpdateLoadingAction
  | ReferralTrackingUpdateErrorAction
  | TimeSheetDataAction;

export const getReferralTracking = (
  payload: GetReferralTrackingApiResponse,
): ReferralTrackingAction => ({
  type: REFERRAL_TRACKING,
  payload,
});

export const getReferralTrackingLoading =
  (): ReferralTrackingLoadingAction => ({
    type: REFERRAL_TRACKING_LOADING,
  });

export const getReferralTrackingError = (
  payload?: GenericError | null,
): ReferralTrackingErrorAction => ({
  type: REFERRAL_TRACKING_ERROR,
  payload,
});

export const getReferralPayouts = (
  payload: GetReferralPayoutsApiResponse,
): ReferralPayoutsAction => ({
  type: REFERRAL_PAYOUTS,
  payload,
});

export const getReferralPayoutsLoading = (): ReferralPayoutsLoadingAction => ({
  type: REFERRAL_PAYOUTS_LOADING,
});

export const getReferralPayoutsError = (
  payload?: GenericError | null,
): ReferralPayoutsErrorAction => ({
  type: REFERRAL_PAYOUTS_ERROR,
  payload,
});

export const getReferralTrackingUpdateActionLoading =
  (): ReferralTrackingUpdateLoadingAction => ({
    type: REFERRAL_TRACKING_UPDATE_ACTION_LOADING,
  });

export const getReferralTrackingUpdateActionError = (
  payload?: GenericError | null,
): ReferralTrackingUpdateErrorAction => ({
  type: REFERRAL_TRACKING_UPDATE_ACTION_ERROR,
  payload,
});

export const getReferralTrackingUpdateActionStatus = (
  payload?: Status | null,
): ReferralTrackingUpdateStatusAction => ({
  type: REFERRAL_TRACKING_UPDATE_ACTION_STATUS,
  payload,
});

export const receiveReceiveTimeSheetData = (
  payload?: Task | null,
): TimeSheetDataAction => ({
  type: RECEIVE_TIME_SHEET_DATA,
  payload,
});

export const getReferralTrackingDetails: (
  payload: ReferralTrackingPayload,
  filterType?: string,
) => ThunkAction<void> = flow(
  key(
    (payload, filterType) =>
      `referral/tracking/?archived=${filterType === 'inactive' ? 'true' : ''}`,
  ),
  fetching(),
)(
  (payload: ReferralTrackingPayload, filterType?: string) =>
    (dispatch: Dispatch) => {
      dispatch(getReferralTrackingLoading());
      const url = `referral/tracking?archived=${
        filterType === 'inactive' ? 'true' : ''
      }`;

      return dispatch(reduxApi.post(url, payload)).then(
        // @ts-expect-error - TS2345 - Argument of type '(response: GetReferralTrackingApiResponse) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
        (response: GetReferralTrackingApiResponse) => {
          dispatch(getReferralTracking(response));
        },
        (error) => {
          dispatch(
            getReferralTrackingError(error?.response?.errors || DEFAULT_ERROR),
          );
        },
      );
    },
);

export const getReferralPayoutsDetails: (
  payload: ReferralTrackingPayload,
  filterType?: string,
) => ThunkAction<void> = flow()(
  (payload: ReferralTrackingPayload, filterType?: string) =>
    (dispatch: Dispatch) => {
      dispatch(getReferralPayoutsLoading());
      return dispatch(
        reduxApi.post(
          `referral/payout?archived=${filterType === 'inactive' ? 'true' : ''}`,
          payload,
        ),
      ).then(
        // @ts-expect-error - TS2345 - Argument of type '(response: GetReferralPayoutsApiResponse) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
        (response: GetReferralPayoutsApiResponse) => {
          dispatch(getReferralPayouts(response));
        },
        (error) => {
          dispatch(
            getReferralPayoutsError(error?.response?.errors || DEFAULT_ERROR),
          );
        },
      );
    },
);

export const updateReferralAction =
  ({
    payload,
    detailsProps,
    name,
    from,
    filterType,
    t,
  }: {
    payload: ReferralUpdateAction[];
    detailsProps: ReferralTrackingPayload;
    name?: string;
    from?: string;
    filterType?: string;
    t: (arg1: string, arg2: string) => string;
  }): ThunkAction<void> =>
  async (dispatch: Dispatch) => {
    const isTracking = from === 'tracking';
    dispatch(getReferralTrackingUpdateActionLoading());
    const archiveMessage = payload[0].message;
    const baseRoute = isTracking ? 'tracking' : 'payout';
    const url = `referral/${baseRoute}${
      archiveMessage ? (isTracking ? '' : 's/archive') : ''
    }`;
    try {
      await dispatch(reduxApi.put(url, {data: payload}));
      if (isTracking) {
        dispatch(getReferralTrackingDetails(detailsProps, filterType || ''));
      } else {
        dispatch(getReferralPayoutsDetails(detailsProps));
      }
      toastApi.show(
        toastMessage({
          // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'boolean | undefined'.
          message: generateMessage(t, payload, name, archiveMessage),
        }),
      );
    } catch (error: any) {
      dispatch(showApiError(error, error?.response?.message));
      dispatch(
        getReferralTrackingUpdateActionError(
          error?.response?.errors || DEFAULT_ERROR,
        ),
      );
    }
  };

export const bulkReferralStatusUpdate =
  (
    filtersPayload: ReferralTrackingPayload,
    status: string,
    from: string,
  ): ThunkAction<unknown> =>
  (dispatch: Dispatch) => {
    let payload;
    if (from === 'tracking') {
      payload = {filters: filtersPayload.filters, status};
    } else {
      payload = {filters: filtersPayload.filters, action: status};
    }
    return dispatch(reduxApi.post(`referral/${from}/bulk-update`, payload))
      .then(() => {
        if (from === 'tracking') {
          dispatch(getReferralTrackingDetails(filtersPayload));
        } else {
          dispatch(getReferralPayoutsDetails(filtersPayload));
        }
        toastApi.show(
          toastMessage({message: `candidates have been Marked As ${status}.`}),
        );
      })
      .catch((error) => {
        dispatch(showApiError(error, error?.response?.message));
      });
  };

export const updatePayoutBonus =
  ({
    payload,
    payoutFilters,
  }: {
    payload: JobsPayload;
    payoutFilters: ReferralTrackingPayload;
  }): ThunkAction<unknown> =>
  (dispatch: Dispatch) => {
    return dispatch(reduxApi.put(`referral/payouts/update`, payload))
      .then(() => {
        return dispatch(getReferralPayoutsDetails(payoutFilters));
      })
      .catch((error) => {
        dispatch(showApiError(error, error?.response?.message));
      });
  };

export const uploadCsvFile =
  (data: FormData): ThunkAction<void> =>
  // @ts-expect-error - TS2322 - Type '(dispatch: Dispatch) => Promise<unknown>' is not assignable to type 'ThunkAction<void>'.
  (dispatch: Dispatch) => {
    return dispatch(reduxApi.post('referral/data-upload', data))
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  };

const generateMessage = (
  t: (arg1: string, arg2: string) => string,
  payload: ReferralUpdateAction[],
  name?: string,
  archiveMessage = false,
): string => {
  const item = payload[0];
  const messageSuffix = archiveMessage ? t('Archived', 'Archived') : '';
  if (payload.length === 1 && name && !item.job_id) {
    return `${name} ${t('has_been_marked_as', 'has been marked as')} ${
      item.status ? t(item.status, item.status) : messageSuffix
    }.`;
  }
  if (!item.status && item.job_id && name) {
    return `${name} ${t(
      'has_been_assigned_a_job',
      'has been assigned a job',
    )}.`;
  }
  AnalyticsService.track('Status Changed', {
    status: item.status || messageSuffix,
  });
  return `${payload.length} ${t(
    'candidates_have_been_marked_as',
    'candidates have been marked as',
  )} ${item.status ? t(item.status, item.status) : messageSuffix}.`;
};
